import { Box, styled, Typography, useMediaQuery, useTheme } from "@mui/material"
import {
  DataGridPro,
  gridClasses,
  gridQuickFilterValuesSelector,
  useGridApiContext,
  useGridSelector,
  type DataGridProProps,
  type GridCallbackDetails,
  type GridFilterModel,
  type GridRowClassNameParams,
  type GridRowSelectionModel,
  type GridSortModel,
  type GridValidRowModel,
} from "@mui/x-data-grid-pro"
import clsx from "clsx"
import type React from "react"
import { useCallback, useMemo, useState, type ReactNode } from "react"
import { extraColors } from "../../utils/theme"
import SvgSortDown from "../icons/SortDown"
import SvgSortUp from "../icons/SortUp"
import { GridCustomToolbar } from "./GridCustomToolbar"
import CustomPaginationControls from "./PaginationControls"

const GridContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  overflow: "auto",
  padding: "28px 32px",
  backgroundColor: extraColors.translucent5,
  minHeight: "75vh",
  borderRadius: 6,
  [theme.breakpoints.down("md")]: {
    padding: "16px",
  },
}))

const Grid = styled(DataGridPro)<{
  "data-hidetoolbar"?: boolean
}>(({ theme, ...props }) => ({
  "--DataGrid-rowBorderColor": extraColors.black,
  "--DataGrid-t-color-border-base": extraColors.black,
  border: "none",
  background: "transparent",
  "& .MuiDataGrid-main": {
    border: "none",

    "& > div:first-of-type": {
      pointerEvents: "inherit !important",
    },
  },

  "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell": {
    whiteSpace: "normal",
  },

  "& .MuiDataGrid-virtualScroller": {
    overflowY: "scroll !important", // Force scrollbar to prevent horizontal jump
  },

  "& .MuiDataGrid-footerContainer": {
    border: "none",
  },

  "& .MuiDataGrid-row:focus-within": {
    outline: `solid 1px ${theme.palette.primary.main}`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    ...theme.typography.small1Bold,
    color: extraColors.white,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
  },
  "& .MuiDataGrid-iconButtonContainer": {
    visibility: "hidden",
    width: 0,
  },
  "& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer": {
    visibility: "visible",
    width: "auto",
  },
  "& .round-chart-header .MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "break-spaces",
    width: "97%",
  },
  "& .MuiDataGrid-iconSeparator": {
    display: "none",
  },
  // This hides the filter adjustment icon above filterable columns.
  // The inputs to the filter menu that this icon exposes aren't very elegant,
  // but if we want to show them to give more granular control, then remove this style.
  "& .MuiDataGrid-filterIcon": {
    display: "none",
  },
  "& .MuiBadge-badge": {
    top: "9px",
    right: "3px",
  },

  "& .MuiDataGrid-menuIcon": {
    marginRight: "5px",
  },
  [`& .${gridClasses.columnHeaders}`]: {
    borderBottom: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    background: extraColors.dark5,
  },
  "& .MuiDataGrid-columnHeader": {
    outline: "none",
    padding: "0px",
    background: "transparent",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cellCheckbox:focus-within":
    {
      outline: "none",
    },
  "& .MuiDataGrid-cell, & .MuiDataGrid-row .MuiDataGrid-cell.rowControlCell": {
    border: "none",
    color: extraColors.white,
  },
  "& .rowControlCell:focus-within": {
    outline: "none",
  },
  ".MuiDataGrid-row:focus-within": {
    outline: "none",
  },
  ".MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-cell:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    width: "calc(100% - 2px)",
    position: "relative",
    padding: 0,
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${extraColors.black}`,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "1px",
    },
  },
  ".MuiIconButton-root": {
    color: extraColors.status.navyLight,
  },
  "& .row-control-remove-button": {
    display: "none",
  },
  "& .MuiDataGrid-row:hover .row-control-remove-button": {
    display: "inherit",
  },
  "& .MuiCheckbox-root svg": {
    color: extraColors.hint,
  },
  "& .MuiDataGrid-selectedRowCount": {
    display: "none",
  },
  "& .centeredCellContent": {
    display: "flex",
  },
  "& .MuiDataGrid-overlay": {
    background: "none",
  },
  "& .MuiDataGrid-toolbarContainer": {
    display: props["data-hidetoolbar"] ? "none" : "inherit",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "transparent",
  },
  [`& .${gridClasses.row}.${gridClasses.row}:hover`]: {
    background: "none",
  },
  [`& .${gridClasses.row}.${gridClasses.row}:hover .${gridClasses.cell}`]: {
    backgroundColor: extraColors.translucentActive,
  },
  [`& .${gridClasses.row}.even .${gridClasses.cell}`]: {
    backgroundColor: extraColors.translucent5,
  },
  [`& .${gridClasses.virtualScrollerRenderZone}`]: {
    width: "100%",
    height: "inherit",
  },
})) as typeof DataGridPro

const StyledGridOverlay = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}))

interface BaseGridProps<
  Row extends GridValidRowModel,
> extends DataGridProProps<Row> {
  filter?: boolean
  filterModel?: GridFilterModel
  onFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails
  ) => void
  sort?: boolean
  search?: boolean
  placeholder?: string
  exported?: boolean
  exportFileName?: string
  headerActionButton?: React.ReactNode
  footerActionButton?: React.ReactNode
  subNav?: React.ReactNode
  //optionals
  noRowsActionElement?: React.ReactNode
  noResultsActionElement?: React.ReactNode
  initSortModel?: GridSortModel
  noTextOverlay?: string
  loading?: boolean
  "data-hidetoolbar"?: boolean
  checkbox?: boolean
  striped?: boolean
  onRowSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => void
  className?: string
}

export function BaseGrid<Row extends Record<string, any>>({
  rows,
  columns,
  filter = false,
  filterModel: filterModelProp,
  onFilterModelChange,
  placeholder,
  sort,
  search = true,
  exported = false,
  exportFileName,
  headerActionButton,
  footerActionButton,
  initSortModel = [],
  noTextOverlay = "No data found",
  noRowsActionElement,
  noResultsActionElement,
  loading,

  checkbox = true,
  columnVisibilityModel = {},
  subNav,
  striped,
  className,
  children,
  getRowClassName,
  slots,
  sx,
  onSortModelChange,
  sortModel: sortModelProp,
  ...props
}: BaseGridProps<Row> & { children?: ReactNode }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  // Controlled sort model if passed in props, otherwise internal state
  const [internalSortModel, setInternalSortModel] =
    useState<GridSortModel>(initSortModel)
  const sortModel = sortModelProp ?? internalSortModel

  const handleSortModelChange = useCallback(
    (model: GridSortModel, details: GridCallbackDetails) => {
      setInternalSortModel(model)
      onSortModelChange?.(model, details)
    },
    [onSortModelChange]
  )

  // Controlled filter model if passed in props, otherwise internal state
  const [internalFilterModel, setInternalFilterModel] =
    useState<GridFilterModel>({ items: [] })
  const filterModel = filterModelProp ?? internalFilterModel

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel, details: GridCallbackDetails) => {
      setInternalFilterModel(model)
      onFilterModelChange?.(model, details)
    },
    [onFilterModelChange]
  )

  const getRowHeight = useCallback(() => {
    return isMobile ? "auto" : 58
  }, [isMobile])

  const combineRowClassNames = useCallback(
    (params: GridRowClassNameParams<Row>) =>
      clsx(
        params.indexRelativeToCurrentPage % 2 === 0 && striped && isMobile
          ? "even"
          : "odd",
        getRowClassName?.(params)
      ),
    [getRowClassName, isMobile, striped]
  )

  const memoizedSlots: DataGridProProps["slots"] = useMemo(
    () => ({
      basePagination: () => (
        <CustomPaginationControls actionButton={footerActionButton} />
      ),
      noResultsOverlay: () => (
        <StyledGridOverlay>
          {!noResultsActionElement ? (
            <Box>
              <NoResultsOverlay />
            </Box>
          ) : (
            noResultsActionElement
          )}
        </StyledGridOverlay>
      ),
      noRowsOverlay: () => (
        <StyledGridOverlay>
          {!noRowsActionElement ? (
            <Typography>{noTextOverlay}</Typography>
          ) : (
            noRowsActionElement
          )}
        </StyledGridOverlay>
      ),
    }),
    [
      footerActionButton,
      noResultsActionElement,
      noRowsActionElement,
      noTextOverlay,
    ]
  )

  return (
    <GridContainer className={className} sx={sx}>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          opacity: loading ? 0.7 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <Grid
          scrollbarSize={0}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          localeText={{
            toolbarExportPrint: "Print or Save as PDF",
          }}
          loading={loading}
          columns={columns}
          rows={rows}
          slots={{
            toolbar: GridCustomToolbar,
            columnSortedAscendingIcon: SvgSortDown,
            columnSortedDescendingIcon: SvgSortUp,
            ...memoizedSlots,
            ...slots,
          }}
          slotProps={{
            toolbar:
              filter || sort || search || exported
                ? {
                    placeholder,
                    filter,
                    search,
                    exported,
                    exportFileName,
                    actionButton: headerActionButton,
                    subNav,
                  }
                : undefined,
          }}
          columnVisibilityModel={columnVisibilityModel}
          initialState={{
            ...props.initialState,
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          checkboxSelection={checkbox}
          disableColumnResize
          disableRowSelectionOnClick
          pagination
          disableColumnPinning
          disableColumnSelector
          disableColumnMenu
          getRowHeight={getRowHeight}
          getRowClassName={combineRowClassNames}
          {...props}
        />
      </Box>
      {children}
    </GridContainer>
  )
}

const NoResultsOverlay = () => {
  const apiRef = useGridApiContext()
  const quickFilterValues = useGridSelector(
    apiRef,
    gridQuickFilterValuesSelector
  )

  return (
    <StyledGridOverlay>
      <Box
        sx={{
          color: extraColors.white,
        }}
      >
        No results for "{quickFilterValues?.join(" ")}"
      </Box>
    </StyledGridOverlay>
  )
}
